import styles from "./ProductDescription.module.css";
import Button from "../Button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { companysvg } from "../../assets/svg/svgimages";
import pfp from "../../assets/images/iconplaceholder.png";
import ArrowIcon from "../../assets/svg/arrowbtn.svg";

import { useDispatch, useSelector } from "react-redux";
import { postCartItem, addFreeToCart, addLocalItem } from "../../store/slices/cartSlice";
import { downloadProductAssetsArchive } from "../../store/slices/singleProductSlice";
import { setAuthRedirectPath } from "../../store/userSlice";
import { useState, useEffect, useRef } from "react";
import { getLensDisplayName } from "../../utils/lens";
import { buildProductAnalyticsProps, capture } from "../../analytics";

const ProductDescription = ({ singleProduct, handleOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.user);
  const { postCartItemStatus, cart } = useSelector((state) => state.cart);
  const { localItems } = useSelector((state) => state.cart);
  const { purchasedProducts } = useSelector((state) => state.profileProduct);

  const isInCart = Array.isArray(cart?.items)
    ? cart.items.some((i) => i.product === singleProduct?.id || i.product_id === singleProduct?.id)
    : false;
  const isInLocal = localItems?.some((i) => i.product === singleProduct?.id) || false;
  const isInAnyCart = isInCart || isInLocal;

  const isPurchased = Array.isArray(purchasedProducts?.results)
    ? purchasedProducts.results.some((p) => p.id === singleProduct.id)
    : false;

  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadPreparing, setShowDownloadPreparing] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const justAddedTimerRef = useRef(null);
  const downloadPreparingTimerRef = useRef(null);
  const prevInCartRef = useRef(isInAnyCart);

  const clearDownloadPreparingTimer = () => {
    if (downloadPreparingTimerRef.current) {
      clearTimeout(downloadPreparingTimerRef.current);
      downloadPreparingTimerRef.current = null;
    }
  };

  const showPreparingBriefly = () => {
    clearDownloadPreparingTimer();
    setShowDownloadPreparing(true);
    downloadPreparingTimerRef.current = setTimeout(() => {
      setShowDownloadPreparing(false);
      downloadPreparingTimerRef.current = null;
    }, 4000);
  };

  useEffect(() => {
    const wasInCart = prevInCartRef.current;
    prevInCartRef.current = isInAnyCart;
    if (!wasInCart && isInAnyCart) {
      setJustAdded(true);
      if (justAddedTimerRef.current) clearTimeout(justAddedTimerRef.current);
      justAddedTimerRef.current = setTimeout(() => {
        setJustAdded(false);
        justAddedTimerRef.current = null;
      }, 1500);
    }
    return () => {
      if (justAddedTimerRef.current) clearTimeout(justAddedTimerRef.current);
    };
  }, [isInAnyCart]);

  useEffect(() => {
    return () => {
      if (downloadPreparingTimerRef.current) {
        clearTimeout(downloadPreparingTimerRef.current);
      }
    };
  }, []);

  const productAccessType = singleProduct?.access_type?.toLowerCase?.() || '';
  const productIsFree = productAccessType === 'free';

  const hasFullPrice = singleProduct?.full_price != null;
  const hasNonGridAssets = Array.isArray(singleProduct?.assets)
    && singleProduct.assets.some(a => a.asset_type?.is_grid === false);
  const effectiveTier = (hasNonGridAssets && hasFullPrice) ? 'full' : 'grids_only';

  const handlePrimaryAction = () => {
    if (isPurchased) { downloadAllAssets(); return; }
    if (isInAnyCart) return;
    if (productIsFree) {
      capture("product_download_clicked", {
        ...buildProductAnalyticsProps(singleProduct),
        download_scope: "free_product",
        is_authenticated: isAuthenticated,
      });
    } else {
      capture("product_add_to_cart_clicked", {
        ...buildProductAnalyticsProps(singleProduct),
        price_tier: effectiveTier,
        is_authenticated: isAuthenticated,
      });
    }
    if (productIsFree && !isAuthenticated) {
      dispatch(setAuthRedirectPath(location.pathname + location.search));
      navigate("/authorization");
      return;
    }
    if (!isAuthenticated) {
      dispatch(addLocalItem({ ...singleProduct, price_tier: effectiveTier }));
      return;
    }
    if (productIsFree) { dispatch(addFreeToCart(singleProduct.id)); return; }
    dispatch(postCartItem({ id: singleProduct.id, priceTier: effectiveTier }));
  };

  const downloadAllAssets = async () => {
    if (!isAuthenticated) {
      dispatch(setAuthRedirectPath(location.pathname + location.search));
      navigate("/authorization");
      return;
    }
    if (isDownloading) return;
    setIsDownloading(true);
    showPreparingBriefly();
    setDownloadError(null);
    capture("product_download_clicked", {
      ...buildProductAnalyticsProps(singleProduct),
      download_scope: singleProduct.purchased_tier === 'grids_only' ? 'grids_only' : 'all_assets',
      is_authenticated: isAuthenticated,
    });
    try {
      await dispatch(downloadProductAssetsArchive({
        productId: singleProduct.id,
        slug: singleProduct.slug,
      })).unwrap();
    } catch (error) {
      capture("download_failed", {
        ...buildProductAnalyticsProps(singleProduct),
        download_scope: singleProduct.purchased_tier === 'grids_only' ? 'grids_only' : 'all_assets',
        error_message: typeof error === "string" ? error : error?.message,
      });
      setDownloadError('Download failed. Please try again.');
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      clearDownloadPreparingTimer();
      setShowDownloadPreparing(false);
      setIsDownloading(false);
    }
  };

  const renderPrice = () => {
    const formatPriceDisplay = (value) => {
      if (value == null) return null;
      const numericValue = Number.parseFloat(value);
      if (Number.isNaN(numericValue)) return `$${value}`;

      const hasFraction = Math.abs(numericValue % 1) > 0.0001;
      return hasFraction ? `$${numericValue.toFixed(2)}` : `$${numericValue.toFixed(0)}`;
    };

    const orig = effectiveTier === 'full' ? singleProduct.full_price : singleProduct.grids_price;
    const disc = effectiveTier === 'full' ? singleProduct.full_discounted_price : singleProduct.grids_discounted_price;
    if (orig == null) return null;
    if (disc && parseFloat(disc) !== parseFloat(orig)) {
      return (
        <span>
          <span className={styles.strikePrice}>{formatPriceDisplay(orig)}</span>
          <span>{formatPriceDisplay(disc)}</span>
        </span>
      );
    }
    return formatPriceDisplay(orig);
  };

  const renderPurchaseContext = () => {
    if (singleProduct.purchased_tier === 'full') {
      return 'Purchased';
    }

    if (singleProduct.purchased_tier === 'grids_only') {
      return 'Grids owned';
    }

    if (productIsFree) {
      return 'Free access';
    }

    return effectiveTier === 'full' ? 'Full package' : 'Grids only';
  };

  const renderPurchaseValue = () => {
    if (singleProduct.purchased_tier === 'full') {
      return 'Full package';
    }

    if (singleProduct.purchased_tier === 'grids_only') {
      return 'Grids only';
    }

    if (productIsFree) {
      return 'Free';
    }

    return renderPrice();
  };

  const totalMB = Array.isArray(singleProduct?.assets)
    ? singleProduct.assets.reduce((sum, a) => sum + (a.file_size || 0), 0) / (1024 * 1024)
    : 0;

  const lensTitle = getLensDisplayName(singleProduct.lens);
  const cameraModelName = singleProduct.camera?.model_name;
  const hasCamdbId = singleProduct.camera?.camdb_id != null;

  const handleStudioLicenseClick = () => {
    capture("studio_license_clicked", buildProductAnalyticsProps(singleProduct));
    handleOpen();
  };

  // ── Author ──
  const renderAuthor = () => {
    const hasUsername = Boolean(singleProduct.author?.username);
    const avatarSrc = singleProduct.author?.profile_picture || pfp;
    const isCompany = !hasUsername;

    const avatar = isCompany
      ? <span className={styles.authorIcon}>{companysvg}</span>
      : <img src={avatarSrc} alt="" className={styles.authorAvatar} />;

    const name = isCompany ? 'Matchmove machine' : singleProduct.author.username;

    const inner = <>{avatar}<span>{name}</span></>;

    if (isCompany) {
      return (
        <a href="https://matchmovemachine.com/" target="_blank" rel="noopener noreferrer" className={styles.authorLink}>
          {inner}
        </a>
      );
    }
    if (productIsFree) {
      return <Link to={`/profile/${singleProduct.author.id}`} className={styles.authorLink}>{inner}</Link>;
    }
    return (
      <a href="https://matchmovemachine.com/" target="_blank" rel="noopener noreferrer" className={styles.authorLink}>
        {inner}
      </a>
    );
  };

  // ── CTA ──
  const renderCTA = () => {
    // Purchased — full
    if (singleProduct.purchased_tier === 'full') {
      return (
        <Button
          variant={isDownloading ? 'grey' : 'blue'}
          label={showDownloadPreparing ? 'Preparing...' : 'Download All'}
          iconType="download"
          onClick={downloadAllAssets}
          disabled={isDownloading}
        />
      );
    }

    // Purchased — grids only
    if (singleProduct.purchased_tier === 'grids_only') {
      return (
        <Button
          variant={isDownloading ? 'grey' : 'blue'}
          label={showDownloadPreparing ? 'Preparing...' : 'Download Grids'}
          iconType="download"
          onClick={downloadAllAssets}
          disabled={isDownloading}
        />
      );
    }

    // Free product
    if (productIsFree) {
      return (
        <>
          <button
            className={`custom-button button-grey ${styles.freeButton}`}
            onClick={handlePrimaryAction}
            type="button"
          >
            <span className={styles.freeLabel}>Download free</span>
            <img src={ArrowIcon} alt="" className={styles.freeArrow} />
          </button>
          {!isAuthenticated && (
            <p className={styles.ctaHelper}>Sign in to download free files</p>
          )}
        </>
      );
    }

    // Paid — just added (brief confirmation state)
    if (justAdded) {
      return <Button variant="green" label="Added ✓" iconType="cart" onClick={handlePrimaryAction} />;
    }

    // Paid — in cart
    if (isInAnyCart) {
      return <Button variant="grey" label="In Cart" iconType="cart" onClick={handlePrimaryAction} />;
    }

    // Paid — buy
    return (
        <Button
          variant={postCartItemStatus === 'loading' ? 'grey' : 'blue'}
          label="Add to cart"
          iconType="cart"
          onClick={handlePrimaryAction}
          disabled={postCartItemStatus === 'loading'}
      />
    );
  };

  return (
    <section className={styles.section} id="description">
      <div className={styles.heroMain}>
        <div className={styles.titleBlock}>
          {lensTitle && (
            <h2 className={styles.lensTitle}>{lensTitle}</h2>
          )}
          <div className={styles.productIdentity}>
            {cameraModelName && (
              <p className={styles.cameraName}>
                <span>{cameraModelName}</span>
                {hasCamdbId && (
                  <a
                    href={`https://camdb.matchmovemachine.com/camera/${singleProduct.camera.camdb_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.camdbLink}
                    aria-label={`Open ${cameraModelName} in CamDB`}
                    onClick={() => capture("camdb_link_clicked", {
                      ...buildProductAnalyticsProps(singleProduct),
                      camdb_id: singleProduct.camera.camdb_id,
                    })}
                  >
                    CamDB ↗
                  </a>
                )}
              </p>
            )}
            {singleProduct.serial_number && (
              <p className={styles.serialNumber}>
                <span className={styles.serialLabel}>Lens serial</span>
                <span className={styles.serialValue}>{singleProduct.serial_number}</span>
              </p>
            )}
          </div>
        </div>

        <div className={styles.purchaseBlock}>
          <span className={styles.purchaseContext}>{renderPurchaseContext()}</span>
          <div className={styles.purchaseShell}>
            <div className={styles.purchaseSummary}>
              <div className={styles.priceValue}>{renderPurchaseValue()}</div>
            </div>
            <div className={styles.ctaBlock}>
              {renderCTA()}
            </div>
          </div>
          {downloadError && <p className={styles.error}>{downloadError}</p>}
        </div>
      </div>

      <div className={styles.metaSection}>
        <div className={styles.metaRow}>
          <span className={styles.byLabel}>by</span>
          {renderAuthor()}
          {totalMB > 0 && (
            <>
              <span className={styles.dot}>·</span>
              <span className={styles.fileSizeMeta}>{Math.round(totalMB)} MB</span>
            </>
          )}
        </div>
        <button className={styles.licensingBtn} onClick={handleStudioLicenseClick}>
          <span className={styles.licensingPrompt}>Need studio licensing?</span>
          <span className={styles.licensingAction}>Contact us →</span>
        </button>
      </div>
    </section>
  );
};

export default ProductDescription;
